import {useEffect, useCallback, useState, useMemo} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

// Chakra
import {
  Box,
  Text,
  Button,
  FormControl,
  FormLabel,
  Switch,
  useDisclosure,
} from '@chakra-ui/react';

import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import { GiveawaysActiveIcon } from 'components/Icons/Icons';
import { apiService } from 'services/ApiService';

import GiveawayPrizes from './components/GiveawayPrizes';
import GiveawayWinners from './components/GiveawayWinners';
import GenerationResultModal from './components/GenerationResultModal';
import NotEnoughWinnersModal from './components/NotEnoughWinnersModal';
import GiveawaysLogs from './components/GiveawaysLogs';
import GiveawayEntries from './components/GiveawayEntries';
import Attention from 'components/Modals/Attention.js';

export default function GiveawaysDetail() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const [
    generateWinnersButtonEnabled,
    setGenerateWinnersButtonEnabled,
  ] = useState(true);
  const [generateWinnersLoading, setGenerateWinnersLoading] = useState(null);
  const [finishButtonEnabled, setFinishButtonEnabled] = useState(true);
  const [hardShowWinners, setHardShowWinners] = useState(true);
  const [showPrizeOrdinal, setShowPrizeOrdinal] = useState(true);
  const [mrrLayout, setMrrLayout] = useState(false);
  const slotMachineUrl = mrrLayout ? '/slot-machine-mrr': '/slot-machine';
  const [isFinishing, setIsFinishing] = useState(false);

  const { data: giveaway, isLoading } = useQuery({
    queryKey: ['giveaway'],
    queryFn: async () => {
      const { data } = await apiService.getGiveaway({ id });

      return data;
    },
  });

  const {
    data: winners,
    isLoading: isWinnersLoading,
    refetch: refetchWinners,
  } = useQuery({
    refetchInterval: 10000,
    queryKey: ['giveawayWinners'],
    queryFn: async () => {
      const { data } = await apiService.getGiveawayWinners({ id });
      return data;
    },
  });

  useEffect(() => {
    if (!winners) {
      return;
    }
    console.log(winners.isWinnersGenerated);
    if (winners.status === 'active') {
      setFinishButtonEnabled(true);
    } else {
      setFinishButtonEnabled(false);
    }
    // setGenerateWinnersButtonEnabled(!winners.isWinnersGenerated);
  }, [winners]);

  const [generationTime, setGenerationTime] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const generateWinners = useCallback(
    async ({ test }) => {
      setGenerateWinnersButtonEnabled(false);
      setGenerateWinnersLoading(test);
      try {
        const { data } = await apiService.generateWinners({
          giveawayId: id,
          test,
        });
        setGenerationTime(data?.generationTime);
        await refetchWinners();
      } finally {
        setGenerateWinnersButtonEnabled(true);
        setGenerateWinnersLoading(null);
      }
      queryClient.invalidateQueries(['giveawayLogs']);
      onOpen();
    },
    [id, refetchWinners]
  );

  const {
    isOpen: isFinishConfirmOpen,
    onOpen: onFinishConfirmOpen,
    onClose: onFinishConfirmClose,
  } = useDisclosure();

  const finishGiveaway = useCallback(async () => {
    if (!winners || isFinishing) {
      return;
    }
    setIsFinishing(true);
    try {
      await apiService.finishGiveaway({ giveawayId: id });
      queryClient.invalidateQueries(['giveaway']);
      await refetchWinners();
      setGenerateWinnersButtonEnabled(false);
    } finally {
      setIsFinishing(false);
      onFinishConfirmClose();
    }
  }, [id, queryClient, refetchWinners, winners, isFinishing, onFinishConfirmClose]);

  const {
    isOpen: isNotEnoughWinnersModalOpen,
    onOpen: onNotEnoughWinnersModalOpen,
    onClose: onNotEnoughWinnersModalClose,
  } = useDisclosure();

  const revealWinners = useCallback(
    (direction) => {
      if (winners?.winners?.length < winners?.prizes?.length) {
        onNotEnoughWinnersModalOpen();
        return;
      }
      const count = winners?.winners?.length || 0;
      const startIndex = direction === 'up' ? 0 : count - 1;
      const params = new URLSearchParams();
      params.set('startIndex', startIndex);
      params.set('direction', direction);
      params.set('showPrizeOrdinal', String(showPrizeOrdinal));
      window.open(
        `/#/game${slotMachineUrl}/${id}?${params.toString()}`,
        '_blank'
      );
    },
    [id, winners, onNotEnoughWinnersModalOpen, showPrizeOrdinal, slotMachineUrl]
  );

  const getUrl = useCallback(() => {
    if (window.location.hash.includes('daily-giveaways')) {
      return '#/daily-giveaways';
    }
    return '#/giveaways';
  }, []);

  return (
    <>
      <Box zIndex="9" position="relative">
        {!isLoading && giveaway && (
          <>
            <Box position="relative">
              <Breadcrumbs
                icon={GiveawaysActiveIcon}
                target="Giveaways"
                url={getUrl()}
                current={`${giveaway.title}`}
              />

              <Button
                onClick={onFinishConfirmOpen}
                disabled={!finishButtonEnabled}
                variant="submit-md-delete"
                mb="34px"
                position="absolute"
                top={0}
                right={0}
                w={250}
              >
                Finish Giveaway
              </Button>
            </Box>

            {giveaway?.declareSetting !== 'auto' && (
              <>
                <Box display="flex" gap={4} px={48} alignItems="center">
                  <Button
                    onClick={() => generateWinners({ test: true })}
                    disabled={!generateWinnersButtonEnabled}
                    variant="warning"
                    mb="34px"
                    isLoading={generateWinnersLoading === true}
                    className={!generateWinnersButtonEnabled ? 'freeze' : ''}
                    loadingText="Generating..."
                  >
                    Generate All Test Winners
                  </Button>
                  <Button
                    onClick={() => generateWinners({ test: false })}
                    disabled={!generateWinnersButtonEnabled}
                    variant="submit"
                    mb="34px"
                    isLoading={generateWinnersLoading === false}
                    className={!generateWinnersButtonEnabled ? 'freeze' : ''}
                    loadingText="Generating..."
                  >
                    Generate All Winners
                  </Button>
                  <Button
                    onClick={() => revealWinners('down')}
                    variant="submit"
                    mb="34px"
                  >
                    Reveal Bottom-&gt;Top
                  </Button>
                  <Button
                    onClick={() => revealWinners('up')}
                    variant="submit"
                    mb="34px"
                  >
                    Reveal Top-&gt;Bottom
                  </Button>
                </Box>
                <Box px={48} mb="34px" display="flex" gap={3}>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel
                      htmlFor="show-prize-ordinal-toggle"
                      mb="0"
                      color="white"
                    >
                      Show prize ordinal (e.g. &quot;1ST PRIZE&quot;)
                    </FormLabel>
                    <Switch
                      id="show-prize-ordinal-toggle"
                      isChecked={showPrizeOrdinal}
                      onChange={(e) => setShowPrizeOrdinal(e.target.checked)}
                    />
                  </FormControl>
                    <FormControl display="flex" alignItems="center">
                        <FormLabel
                            htmlFor="mmr-layout-toggle"
                            mb="0"
                            color="white"
                        >
                            Enable MRR Layout ?
                        </FormLabel>
                        <Switch
                            id="mmr-layout-toggle"
                            isChecked={mrrLayout}
                            onChange={(e) => setMrrLayout(e.target.checked)}
                        />
                    </FormControl>
                </Box>

              </>
            )}
            {winners && (
              <>
                <Text
                  variant="title-lg"
                  mt="40px"
                  mb="14px"
                  onClick={() => setHardShowWinners(!hardShowWinners)}
                >
                  Winners
                </Text>
                <GiveawayWinners
                  slotMachineUrl={slotMachineUrl}
                  isEditable={giveaway?.declareSetting !== 'auto'}
                  hardShowWinners={hardShowWinners}
                  giveaway={winners}
                  showPrizeOrdinal={showPrizeOrdinal}
                />
              </>
            )}
            <Text variant="title-lg" mt="40px" mb="14px">
              Prizes
            </Text>
            <GiveawayPrizes giveaway={giveaway} />
            {/* Giveaway ID for auditing, placed near Entries */}
            <Text
              variant="text-md"
              fontWeight="bold"
              mt="40px"
              mb="2"
              color="white"
            >
              Giveaway ID: {giveaway.id}
            </Text>
            <Text variant="title-lg" mt="40px" mb="14px">
              Entries
            </Text>
            <GiveawayEntries giveawayId={giveaway.id} />
          </>
        )}

        {giveaway?.declareSetting !== 'auto' && (
          <GiveawaysLogs giveawayId={id} />
        )}
      </Box>
      <GenerationResultModal
        isOpen={isOpen}
        onClose={onClose}
        generationTime={generationTime}
      />
      <NotEnoughWinnersModal
        isOpen={isNotEnoughWinnersModalOpen}
        onClose={onNotEnoughWinnersModalClose}
      />
      <Attention
        variant="finish"
        isOpen={isFinishConfirmOpen}
        isLoading={isFinishing}
        handle={finishGiveaway}
        onClose={() => {
          if (isFinishing) {
            return;
          }
          onFinishConfirmClose();
        }}
      />
    </>
  );
}
